import type { DashboardMetric, DashboardMetricsResponse } from '../../../types/dashboard';
import { getConfiguredSubawardeeIdsForComponentProject } from '../../../config/componentProjects';
import { MAX_QUARTERS_CHART } from '../constants';
import { readPointFieldGoal } from '../chartGoalUtils';
import { parseQuarter } from '../metricSeriesUtils';
import {
  collectActiveSubawardeeIds,
  readSubawardeeFieldBreakdown,
} from '../subawardeeBreakdownUtils';
import type { ArchMetricsData, ArchMetricsPoint } from './types';

const ASSISTED_KEYS = ['companies_assisted', 'companiesAssisted', 'assisted'];
const CERTIFIED_KEYS = ['companies_certified', 'companiesCertified', 'certified'];

/** Grant target: 35 certifications/year by Y4Q4 (arch-milestones.json). */
export const ARCH_CERTIFICATIONS_ANNUAL_GOAL = 35;

const SERIES_META_KEYS = new Set(['quarter', 'qoq', 'yoy', 'goal', 'goalStatus']);

function quarterSortKey(label: string): number {
  const parsed = parseQuarter(label);
  if (!parsed) return 0;
  return parsed.year * 10 + parsed.quarter;
}

function readNumericField(point: Record<string, unknown>, keys: string[]): number {
  for (const key of keys) {
    const value = point[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
}

function inferArchFieldFromMetricId(metricId: string): keyof ArchMetricsPoint | null {
  const id = metricId.toLowerCase();
  if (id.includes('companies_certified') || id.includes('companiescertified') || (id.includes('certif') && id.includes('compan'))) {
    return 'companiesCertified';
  }
  if (id.includes('companies_assisted') || id.includes('companiesassisted') || (id.includes('assist') && id.includes('compan'))) {
    return 'companiesAssisted';
  }
  if (id.includes('certified') || id.includes('certification')) {
    return 'companiesCertified';
  }
  if (id.includes('assisted')) {
    return 'companiesAssisted';
  }
  return null;
}

function applyMetricLevelGoals(
  merged: Record<string, unknown>,
  quarter: string,
  metric: DashboardMetric
): void {
  const activeGoals = (metric as DashboardMetric & { goals?: { active?: Array<{
    grain?: string;
    periodKey?: string;
    targets?: Record<string, { operator?: string; value?: number }>;
  }> } }).goals?.active;
  if (!Array.isArray(activeGoals) || activeGoals.length === 0) return;

  const meta = parseQuarter(quarter);
  if (!meta) return;

  const existingGoal =
    merged.goal && typeof merged.goal === 'object'
      ? ({ ...(merged.goal as Record<string, unknown>) } as Record<string, unknown>)
      : {};

  for (const goal of activeGoals) {
    const isAnnual = goal.grain === 'annual' && goal.periodKey === String(meta.year);
    const isQuarterly =
      goal.grain === 'quarter' && goal.periodKey === `${meta.year}-Q${meta.quarter}`;
    if (!isAnnual && !isQuarterly) continue;

    for (const [fieldName, target] of Object.entries(goal.targets ?? {})) {
      if (existingGoal[fieldName]) continue;
      if (typeof target.value === 'number' && Number.isFinite(target.value)) {
        existingGoal[fieldName] = {
          operator: target.operator,
          target: target.value,
        };
      }
    }
  }

  if (Object.keys(existingGoal).length > 0) {
    merged.goal = existingGoal;
  }
}

function promoteInferredArchMetricGoal(
  merged: Record<string, unknown>,
  metric: DashboardMetric
): void {
  const inferredField = inferArchFieldFromMetricId(metric.metricId);
  if (!inferredField) return;

  const goal = merged.goal;
  if (!goal || typeof goal !== 'object') return;

  const goalRecord = goal as Record<string, unknown>;
  const valueGoal = goalRecord.value;
  if (!valueGoal || typeof valueGoal !== 'object') return;

  const storageKey = inferredField === 'companiesAssisted' ? 'companies_assisted' : 'companies_certified';
  if (!goalRecord[storageKey] && !goalRecord[inferredField]) {
    merged.goal = {
      ...goalRecord,
      [storageKey]: valueGoal,
      [inferredField]: valueGoal,
    };
  }
}

function mergePointGoals(
  merged: Record<string, unknown>,
  point: Record<string, unknown>
): void {
  if (!point.goal || typeof point.goal !== 'object') return;
  const existing =
    merged.goal && typeof merged.goal === 'object'
      ? (merged.goal as Record<string, unknown>)
      : {};
  merged.goal = { ...existing, ...(point.goal as Record<string, unknown>) };
}

function toArchPoint(quarter: string, raw: Record<string, unknown>): ArchMetricsPoint {
  const companiesAssistedGoal = readPointFieldGoal(raw, ASSISTED_KEYS) ?? undefined;
  const companiesCertifiedGoal =
    readPointFieldGoal(raw, CERTIFIED_KEYS) ?? ARCH_CERTIFICATIONS_ANNUAL_GOAL;
  const companiesCertifiedBySubawardee = raw.companiesCertifiedBySubawardee as
    | Record<string, number>
    | undefined;

  return {
    quarter,
    companiesAssisted: readNumericField(raw, ASSISTED_KEYS),
    companiesCertified: readNumericField(raw, CERTIFIED_KEYS),
    ...(companiesCertifiedBySubawardee && Object.keys(companiesCertifiedBySubawardee).length > 0
      ? { companiesCertifiedBySubawardee }
      : {}),
    goalTargets: {
      ...(companiesAssistedGoal !== undefined ? { companiesAssisted: companiesAssistedGoal } : {}),
      companiesCertified: companiesCertifiedGoal,
    },
  };
}

export function mapArchMetricsData(
  data: DashboardMetricsResponse | undefined
): ArchMetricsData | null {
  if (!data?.sections?.length) return null;

  const subawardeeIds = getConfiguredSubawardeeIdsForComponentProject('arch');
  const byQuarter = new Map<string, Record<string, unknown>>();
  const allTimeRaw: Record<string, unknown> = {};
  let certifiedByQuarter = new Map<string, Record<string, number>>();

  for (const section of data.sections) {
    for (const metric of section.metrics) {
      const certifiedBreakdown = readSubawardeeFieldBreakdown(
        metric,
        subawardeeIds,
        CERTIFIED_KEYS
      );
      if (certifiedBreakdown) {
        certifiedByQuarter = certifiedBreakdown;
      }

      const inferredField = inferArchFieldFromMetricId(metric.metricId);

      for (const point of metric.series) {
        const quarter = String(point.quarter ?? '');
        if (!quarter) continue;

        const merged = byQuarter.get(quarter) ?? { quarter };
        for (const [key, value] of Object.entries(point)) {
          if (!SERIES_META_KEYS.has(key) && typeof value === 'number' && Number.isFinite(value)) {
            merged[key] = value;
          }
        }

        if (inferredField && typeof point.value === 'number' && Number.isFinite(point.value)) {
          const storageKey =
            inferredField === 'companiesAssisted' ? 'companies_assisted' : 'companies_certified';
          merged[storageKey] = point.value;
        }

        mergePointGoals(merged, point);
        applyMetricLevelGoals(merged, quarter, metric);
        promoteInferredArchMetricGoal(merged, metric);
        byQuarter.set(quarter, merged);
      }

      if (metric.allTime) {
        for (const [key, value] of Object.entries(metric.allTime)) {
          if (typeof value === 'number' && Number.isFinite(value)) {
            allTimeRaw[key] = value;
          }
        }
        if (inferredField && typeof metric.allTime.value === 'number') {
          const storageKey =
            inferredField === 'companiesAssisted' ? 'companies_assisted' : 'companies_certified';
          allTimeRaw[storageKey] = metric.allTime.value;
        }
      }
    }
  }

  const series = Array.from(byQuarter.entries())
    .sort(([a], [b]) => quarterSortKey(a) - quarterSortKey(b))
    .slice(-MAX_QUARTERS_CHART)
    .map(([quarter, raw]) => {
      const certifiedForQuarter = certifiedByQuarter.get(quarter);
      if (certifiedByQuarter.size > 0) {
        raw.companiesCertifiedBySubawardee = certifiedForQuarter ?? {};
      }
      return toArchPoint(quarter, raw);
    });

  const certifiedSubawardeeIds = collectActiveSubawardeeIds(certifiedByQuarter, subawardeeIds);

  if (series.length === 0) return null;

  return {
    series,
    allTime: {
      companiesAssisted: readNumericField(allTimeRaw, ASSISTED_KEYS),
      companiesCertified: readNumericField(allTimeRaw, CERTIFIED_KEYS),
    },
    certifiedSubawardeeIds,
  };
}

export function hasArchCompanyMetricsData(data: ArchMetricsData): boolean {
  return (
    data.series.some((point) => point.companiesAssisted > 0 || point.companiesCertified > 0) ||
    data.allTime.companiesAssisted > 0 ||
    data.allTime.companiesCertified > 0 ||
    data.certifiedSubawardeeIds.length > 0
  );
}

export function hasArchCertifiedSubawardeeBreakdown(data: ArchMetricsData): boolean {
  return data.certifiedSubawardeeIds.length > 0;
}

function isCompanyKpisMetricId(metricId: string): boolean {
  const id = metricId.toLowerCase();
  return (
    id.includes('programkpi') ||
    id.includes('program_kpi') ||
    id.includes('companykpi') ||
    id.includes('company_kpi') ||
    id.includes('greenuaskpi')
  );
}

function metricHasArchCompanyFields(metric: DashboardMetric): boolean {
  if (inferArchFieldFromMetricId(metric.metricId)) return true;

  const hasArchKeys = (record: Record<string, unknown>): boolean =>
    Object.keys(record).some((key) => {
      const normalized = key.toLowerCase();
      return (
        ASSISTED_KEYS.some((k) => normalized.includes(k.replace(/_/g, ''))) ||
        CERTIFIED_KEYS.some((k) => normalized.includes(k.replace(/_/g, ''))) ||
        normalized.includes('companies_assisted') ||
        normalized.includes('companies_certified') ||
        normalized.includes('companiesassisted') ||
        normalized.includes('companiescertified')
      );
    });

  if (metric.series.some((point) => hasArchKeys(point))) return true;
  if (metric.allTime && hasArchKeys(metric.allTime)) return true;
  return false;
}

export function isArchCompanyMetric(metricId: string, metric?: DashboardMetric): boolean {
  if (isCompanyKpisMetricId(metricId)) return true;
  if (inferArchFieldFromMetricId(metricId)) return true;
  if (metric && metricHasArchCompanyFields(metric)) return true;
  return false;
}
